import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Eye } from "lucide-react";

function NewThreadDialog({ onCreated, presetRecipientId, presetOpen, onPresetHandled }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [target, setTarget] = useState("admin"); // "admin" | "supplier" | "buyer"
  const [users, setUsers] = useState([]);
  const [recipient, setRecipient] = useState("");
  const [suppliersList, setSuppliersList] = useState([]);

  // Load recipient candidates based on target + role
  useEffect(() => {
    if (!open) return;
    if (target === "admin") { setRecipient(""); return; }
    if (user.role === "buyer" && target === "supplier") {
      api.get("/suppliers").then((r) => setSuppliersList(r.data)).catch(() => {});
    }
    if (user.role === "supplier" && target === "buyer") {
      api.get("/admin/users?role=buyer&status=approved").then((r) => setUsers(r.data)).catch(() => setUsers([]));
    }
    if (user.role === "admin") {
      api.get("/admin/users").then((r) => setUsers(r.data.filter((u) => u.id !== user.id))).catch(() => {});
    }
  }, [open, user, target]);

  // Auto-open with a preset supplier recipient (from ?to=<sid>)
  useEffect(() => {
    if (presetOpen && presetRecipientId && user?.role === "buyer") {
      setTarget("supplier");
      setRecipient(presetRecipientId);
      setSubject("Sourcing inquiry");
      setOpen(true);
      onPresetHandled?.();
    }
  }, [presetOpen, presetRecipientId, user, onPresetHandled]);

  const submit = async () => {
    try {
      let response;
      if (user.role === "admin") {
        response = await api.post("/admin/threads", { recipient_id: recipient, subject, body });
      } else {
        response = await api.post("/threads", {
          subject, body,
          recipient_role: target,
          recipient_id: target === "admin" ? null : recipient,
        });
      }
      const tid = response.data.thread_id;
      onCreated(tid); setOpen(false); setSubject(""); setBody(""); setRecipient(""); setTarget("admin");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const targetChoices = user?.role === "buyer"
    ? [{ v: "admin", l: "Safeway Finland team" }, { v: "supplier", l: "A supplier directly (admin CC'd)" }]
    : user?.role === "supplier"
      ? [{ v: "admin", l: "Safeway Finland team" }, { v: "buyer", l: "A buyer directly (admin CC'd)" }]
      : [];

  const recipientOptions = user?.role === "buyer" && target === "supplier"
    ? suppliersList.map((s) => ({ id: s.id, label: `${s.company_name || s.name}${s.country ? ` · ${s.country}` : ""}` }))
    : users.map((u) => ({ id: u.id, label: `${u.company_name || u.name} (${u.role} · ${u.email})` }));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-thread-btn">+ New message</Button></DialogTrigger>
      <DialogContent data-testid="new-thread-dialog">
        <DialogHeader><DialogTitle className="font-heading tracking-tight">Start a conversation</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {user.role !== "admin" && (
            <div>
              <Label>Send to</Label>
              <select data-testid="thread-target" className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={target} onChange={(e) => { setTarget(e.target.value); setRecipient(""); }}>
                {targetChoices.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </div>
          )}
          {((user.role === "admin") || (user.role !== "admin" && target !== "admin")) && (
            <div>
              <Label>Recipient</Label>
              <select data-testid="thread-recipient" className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={recipient} onChange={(e) => setRecipient(e.target.value)}>
                <option value="">Select…</option>
                {recipientOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
              {user.role !== "admin" && (
                <p className="mt-1 text-xs text-muted-foreground">The Safeway Finland team will be automatically CC'd for compliance visibility.</p>
              )}
            </div>
          )}
          {user.role !== "admin" && target === "admin" && (
            <div className="text-sm text-muted-foreground">Your message will go to the Safeway Finland operations team.</div>
          )}
          <div><Label>Subject</Label><Input data-testid="thread-subject" value={subject} onChange={(e) => setSubject(e.target.value)} /></div>
          <div><Label>Message</Label><Textarea data-testid="thread-body" rows={4} value={body} onChange={(e) => setBody(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="thread-send" onClick={submit} disabled={!body.trim() || (user.role === "admin" && !recipient) || (user.role !== "admin" && target !== "admin" && !recipient)}>Send</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Messages() {
  const { threadId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [current, setCurrent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");
  const [presetOpen, setPresetOpen] = useState(false);
  const listEndRef = useRef(null);

  const loadThreads = async () => {
    try { const { data } = await api.get("/threads"); setThreads(data); } catch {}
  };
  const openThread = async (id) => {
    nav(`/messages/${id}`);
    try {
      const { data } = await api.get(`/threads/${id}`);
      setCurrent(data.thread); setMessages(data.messages || []);
      setTimeout(() => listEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch {}
  };

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => { if (threadId) openThread(threadId); /* eslint-disable-next-line */ }, [threadId]);

  // Handle ?to=<supplier_id> shortcut from storefront
  useEffect(() => {
    const to = params.get("to");
    if (to) setPresetOpen(true);
  }, [params]);

  const send = async () => {
    if (!msg.trim() || !current) return;
    try {
      await api.post(`/threads/${current.id}/messages`, { body: msg });
      setMsg("");
      const { data } = await api.get(`/threads/${current.id}`);
      setMessages(data.messages || []);
      loadThreads();
      setTimeout(() => listEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const presetRecipientId = params.get("to");

  return (
    <DashboardLayout title="Messages" actions={
      <NewThreadDialog
        onCreated={(id) => { loadThreads(); openThread(id); }}
        presetRecipientId={presetRecipientId}
        presetOpen={presetOpen}
        onPresetHandled={() => { setPresetOpen(false); params.delete("to"); setParams(params, { replace: true }); }}
      />
    }>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 h-[calc(100vh-13rem)] min-h-[500px]">
        <div className="lg:col-span-1 bg-white border border-border rounded-lg overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-border font-heading font-semibold text-sm text-secondary">Conversations</div>
          <div className="flex-1 overflow-y-auto">
            {threads.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground" data-testid="threads-empty">No conversations yet</div>}
            {threads.map((t) => (
              <button
                key={t.id}
                data-testid={`thread-item-${t.id}`}
                onClick={() => openThread(t.id)}
                className={`w-full text-left px-4 py-3 border-b border-border hover:bg-muted/40 ${current?.id === t.id ? "bg-muted/50" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-sm text-secondary truncate">{t.counterpart?.name || "Safeway Finland"}</div>
                  <div className="flex items-center gap-1">
                    {t.is_cc && <Badge variant="outline" className="text-[9px] px-1.5 py-0"><Eye className="w-2.5 h-2.5 mr-0.5" />CC</Badge>}
                    {t.unread > 0 && <Badge className="bg-primary text-primary-foreground text-[10px]">{t.unread}</Badge>}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground truncate mt-0.5">{t.subject}</div>
              </button>
            ))}
          </div>
        </div>
        <div className="lg:col-span-2 bg-white border border-border rounded-lg overflow-hidden flex flex-col">
          {!current ? (
            <div className="flex-1 grid place-items-center text-sm text-muted-foreground" data-testid="no-thread-selected">Select a conversation</div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-border">
                <div className="font-heading font-semibold text-secondary">{current.subject}</div>
                <div className="text-xs text-muted-foreground">
                  {current.counterpart?.name} · {current.counterpart?.role}
                  {current.cc?.length > 0 && (
                    <span className="ml-2 inline-flex items-center gap-1 text-primary"><Eye className="w-3 h-3" />CC: {current.cc.map((c) => c.name).join(", ")}</span>
                  )}
                  {current.is_cc && <span className="ml-2 inline-flex items-center gap-1 text-primary"><Eye className="w-3 h-3" />You are observing (CC)</span>}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-list">
                {messages.map((m) => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] px-4 py-2.5 rounded-lg text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-muted text-secondary"}`} data-testid={`message-${m.id}`}>
                        <div className="whitespace-pre-wrap leading-relaxed">{m.body}</div>
                        <div className={`text-[10px] mt-1 ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{new Date(m.created_at).toLocaleString()}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={listEndRef} />
              </div>
              <div className="p-3 border-t border-border flex gap-2">
                <Input data-testid="message-input" placeholder={current.is_cc ? "Add a note (visible to both sides)…" : "Type a message…"} value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
                <Button data-testid="message-send" onClick={send} disabled={!msg.trim()}>Send</Button>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
